<script setup lang="ts">
  /**
   * Marketing Layout
   *
   * Layout for public marketing site (claimshieldpro.com).
   * Features:
   * - Clean header with navigation
   * - Footer with links
   * - No authentication required
   */
  import { ref } from 'vue'
  import { useRoute } from 'vue-router'

  const route = useRoute()
  const mobileMenuOpen = ref(false)

  // Navigation items
  const navItems = [
    { label: 'Transportation', route: '/transportation' },
    { label: 'Industrial', route: '/industrial' },
    { label: 'About', route: '/about' },
    { label: 'Resources', route: '/resources' },
    { label: 'Contact', route: '/contact' },
  ]

  const footerLinks = {
    services: [
      { label: 'MVR Services', route: '/transportation/mvr' },
      { label: 'PSP Services', route: '/transportation/psp' },
      { label: 'Driver Qualification', route: '/transportation/driver-qualification' },
      { label: 'OSHA Compliance', route: '/industrial/osha-compliance' },
    ],
    company: [
      { label: 'About Us', route: '/about' },
      { label: 'Contact', route: '/contact' },
      { label: 'Resources', route: '/resources' },
    ],
    legal: [
      { label: 'Privacy Policy', route: '/privacy' },
      { label: 'Terms of Service', route: '/terms' },
    ],
  }

  const isNavActive = (itemRoute: string) => {
    if (itemRoute === '/') return route.path === '/'
    return route.path.startsWith(itemRoute)
  }
</script>

<template>
  <div class="flex min-h-screen flex-col">
    <!-- Header -->
    <header class="sticky top-0 z-50 border-b border-secondary-200 bg-white">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="flex h-16 items-center justify-between">
          <!-- Logo -->
          <RouterLink to="/" class="flex items-center gap-2">
            <span class="text-2xl font-bold text-primary-600">ClaimShield</span>
          </RouterLink>

          <!-- Desktop navigation -->
          <nav class="hidden items-center gap-8 lg:flex">
            <RouterLink
              v-for="item in navItems"
              :key="item.route"
              :to="item.route"
              :class="[
                'text-sm font-medium transition-colors',
                isNavActive(item.route)
                  ? 'text-primary-600'
                  : 'text-secondary-600 hover:text-secondary-900',
              ]"
            >
              {{ item.label }}
            </RouterLink>
          </nav>

          <!-- CTA buttons -->
          <div class="hidden items-center gap-3 lg:flex">
            <RouterLink
              to="/login"
              class="text-sm font-medium text-secondary-600 hover:text-secondary-900"
            >
              Sign In
            </RouterLink>
            <RouterLink
              to="/contact"
              class="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              Get Started
            </RouterLink>
          </div>

          <!-- Mobile menu button -->
          <button
            class="rounded-lg p-2 text-secondary-600 hover:bg-secondary-100 lg:hidden"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <i :class="mobileMenuOpen ? 'pi pi-times' : 'pi pi-bars'" class="text-xl" />
          </button>
        </div>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="border-t border-secondary-200 bg-white lg:hidden">
        <nav class="space-y-1 px-4 py-3">
          <RouterLink
            v-for="item in navItems"
            :key="item.route"
            :to="item.route"
            class="block rounded-lg px-3 py-2 text-base font-medium text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
            @click="mobileMenuOpen = false"
          >
            {{ item.label }}
          </RouterLink>
          <div class="border-t border-secondary-200 pt-3">
            <RouterLink
              to="/login"
              class="block rounded-lg px-3 py-2 text-base font-medium text-secondary-600 hover:bg-secondary-100"
              @click="mobileMenuOpen = false"
            >
              Sign In
            </RouterLink>
            <RouterLink
              to="/contact"
              class="mt-2 block rounded-lg bg-primary-600 px-3 py-2 text-center text-base font-medium text-white"
              @click="mobileMenuOpen = false"
            >
              Get Started
            </RouterLink>
          </div>
        </nav>
      </div>
    </header>

    <!-- Main content -->
    <main class="flex-1">
      <RouterView />
    </main>

    <!-- Footer -->
    <footer class="border-t border-secondary-200 bg-secondary-900 text-secondary-300">
      <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 gap-8 md:grid-cols-4">
          <!-- Brand -->
          <div>
            <span class="text-xl font-bold text-white">ClaimShield</span>
            <p class="mt-4 text-sm">
              Safety and compliance services for trucking and OSHA-regulated industries.
            </p>
          </div>

          <!-- Services -->
          <div>
            <h3 class="text-sm font-semibold uppercase tracking-wider text-white">Services</h3>
            <ul class="mt-4 space-y-2">
              <li v-for="link in footerLinks.services" :key="link.route">
                <RouterLink
                  :to="link.route"
                  class="text-sm hover:text-white"
                >
                  {{ link.label }}
                </RouterLink>
              </li>
            </ul>
          </div>

          <!-- Company -->
          <div>
            <h3 class="text-sm font-semibold uppercase tracking-wider text-white">Company</h3>
            <ul class="mt-4 space-y-2">
              <li v-for="link in footerLinks.company" :key="link.route">
                <RouterLink
                  :to="link.route"
                  class="text-sm hover:text-white"
                >
                  {{ link.label }}
                </RouterLink>
              </li>
            </ul>
          </div>

          <!-- Legal -->
          <div>
            <h3 class="text-sm font-semibold uppercase tracking-wider text-white">Legal</h3>
            <ul class="mt-4 space-y-2">
              <li v-for="link in footerLinks.legal" :key="link.route">
                <RouterLink
                  :to="link.route"
                  class="text-sm hover:text-white"
                >
                  {{ link.label }}
                </RouterLink>
              </li>
            </ul>
          </div>
        </div>

        <!-- Copyright -->
        <div class="mt-8 border-t border-secondary-700 pt-8">
          <p class="text-center text-sm">
            &copy; {{ new Date().getFullYear() }} ClaimShield. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>
